import { useNavigate } from 'react-router-dom';

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { assetsApi } from '../axios';
import { useLanguage } from '../hooks/useLanguage';
import { AssetRequest, AssetTypeDetail, ImportRowError } from '../types/asset';
import { paths } from '../utils/navigation';

export function useAssetsQuery(search: string, page: number, assetTypeId?: number) {
  return useQuery({
    queryKey: ['assets', search, page, assetTypeId],
    queryFn: () => assetsApi.getAssets(search, page, assetTypeId),
  });
}

export function useAssetsInfiniteQuery(searchQuery: string) {
  return useInfiniteQuery({
    queryKey: ['assets', searchQuery],
    queryFn: ({ pageParam = 1 }) => assetsApi.getAssets(searchQuery, pageParam),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page > 1) {
        return firstPage.page - 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });
}

export function useAssetQuery(assetId: number) {
  return useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => assetsApi.getAsset(assetId),
    enabled: !!assetId,
  });
}

export function useAssetPermitsQuery(assetId: number | undefined) {
  return useQuery({
    queryKey: ['assetPermits', assetId],
    queryFn: () => assetsApi.getAssetPermits(assetId),
    enabled: !!assetId,
  });
}

export function useAssetMetersQuery(assetId: number, redirectOnError = true) {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['assetMeters', assetId],
    queryFn: () => assetsApi.getAssetMeters(assetId),
    enabled: !!assetId,
    staleTime: 0,
  });

  if (query.error && !query.isLoading && redirectOnError) {
    const axiosError = query.error as AxiosError | undefined;
    if (axiosError?.response?.status === 404) {
      // Use setTimeout to ensure navigation happens after render
      setTimeout(() => {
        navigate(paths.assets.types, { replace: true });
      }, 0);
    }
  }

  return query;
}

export function useAssetMeterMeasurementsQuery(assetId: number, meterId: string, page: number) {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['assetMeterMeasurements', assetId, meterId, page],
    queryFn: () => assetsApi.getAssetMeterMeasurements(assetId, meterId, page),
  });

  if (query.error && !query.isLoading) {
    const axiosError = query.error as AxiosError | undefined;
    if (axiosError?.response?.status === 404) {
      // Use setTimeout to ensure navigation happens after render
      setTimeout(() => {
        navigate(paths.assets.types, { replace: true });
      }, 0);
    }
  }

  return query;
}

export function useAssetMeterMeasurementsHistoryQuery(
  assetId: number,
  meterId: string,
  page: number,
) {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['assetMeterMeasurementsHistory', assetId, meterId, page],
    queryFn: () => assetsApi.getAssetMeterMeasurementsHistory(assetId, meterId, page),
  });

  if (query.error && !query.isLoading) {
    const axiosError = query.error as AxiosError | undefined;
    if (axiosError?.response?.status === 404) {
      setTimeout(() => {
        navigate(paths.assets.types, { replace: true });
      }, 0);
    }
  }

  return query;
}

export function useUpdateAssetMeterMeasurementMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      assetId,
      meterId,
      measurementId,
      value,
    }: {
      assetId: number;
      meterId: string;
      measurementId: string;
      value: number;
    }) => assetsApi.updateAssetMeterMeasurement(assetId, meterId, measurementId, value),
    onSuccess: (_data, variables) => {
      // Invalidate measurements for all pages for this asset/meter
      enqueueSnackbar(lang.assets.measurementUpdated, { variant: 'success' });
      queryClient.invalidateQueries({
        queryKey: ['assetMeterMeasurements', variables.assetId, variables.meterId],
        exact: false,
      });
    },
    onError: () => {
      enqueueSnackbar(lang.assets.measurementUpdatedError, { variant: 'error' });
    },
  });
}

export function useDeleteAssetMeterMeasurementMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      assetId,
      meterId,
      measurementId,
    }: {
      assetId: number;
      meterId: string;
      measurementId: number;
    }) => assetsApi.deleteAssetMeterMeasurement(assetId, meterId, measurementId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['assetMeterMeasurements', variables.assetId, variables.meterId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['assets'],
        exact: false,
      });
      enqueueSnackbar(lang.assets.measurementDeleted ?? lang.assets.delete, { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(lang.assets.measurementDeletedError ?? lang.assets.assetDeletedError, {
        variant: 'error',
      });
    },
  });
}

export function useAssignUsersToAssetMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assetId,
      userIds,
      groupIds,
    }: {
      assetId: number;
      userIds: number[];
      groupIds: number[];
    }) => {
      return assetsApi.assignUsersToAsset(assetId, userIds, groupIds);
    },
    onSuccess: () => {
      // Invalidate queries to update local state
      queryClient.invalidateQueries({ queryKey: ['assets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetPermits'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
    },
  });
}

export function useAssetsHistoryQuery(search: string, page: number, assetTypeId?: number) {
  return useQuery({
    queryKey: ['assetsHistory', search, page, assetTypeId],
    queryFn: () => assetsApi.getAssetsHistory(search, page, assetTypeId),
  });
}

export function useCreateAssetMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (asset: AssetRequest) => {
      return assetsApi.createAsset(asset);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetLimitStatus'] });
      enqueueSnackbar(
        data.revived ? lang.assets.assetRestored : lang.assets.assetCreated,
        { variant: 'success' },
      );
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      const errorMessages = error?.response?.data?.errors;

      // Check for duplicate code error
      const isDuplicateCode = errorMessages?.some((errorMessage) =>
        errorMessage.includes('duplicate-code'),
      );

      if (isDuplicateCode) {
        enqueueSnackbar(lang.assets.assetCodeAlreadyExists, { variant: 'error' });
      } else {
        enqueueSnackbar(lang.assets.assetCreatedError, { variant: 'error' });
      }
    },
  });
}

export function useUpdateAssetMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ assetId, asset }: { assetId: number; asset: AssetRequest }) => {
      return assetsApi.updateAsset(assetId, asset);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['asset'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetPermits'], exact: false });
      enqueueSnackbar(lang.assets.assetUpdated, { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(lang.assets.assetUpdatedError, { variant: 'error' });
    },
  });
}

export function useToggleAssetStatusMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ assetId }: { assetId: number; isInactive: boolean }) => {
      return assetsApi.toggleAssetStatus(assetId);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['asset'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetsHistory'], exact: false });
      const message =
        data.status === 'active' ? lang.assets.assetReactivated : lang.assets.assetDeactivated;
      enqueueSnackbar(message, { variant: 'success' });
    },
    onError: (_error, { isInactive }) => {
      const message = isInactive
        ? lang.assets.assetReactivatedError
        : lang.assets.assetDeactivatedError;
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useDeleteAssetMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (assetId: number) => {
      return assetsApi.deleteAsset(assetId);
    },
    onSuccess: () => {
      // Invalidate queries to update local state
      queryClient.invalidateQueries({ queryKey: ['assets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['asset'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetsHistory'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetLimitStatus'] });
      enqueueSnackbar(lang.assets.assetDeleted, { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(lang.assets.assetDeletedError, { variant: 'error' });
    },
  });
}

export function useBulkDeleteAssetsMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (assetIds: number[]) => assetsApi.bulkDeleteAssets(assetIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetsHistory'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetLimitStatus'] });
      enqueueSnackbar(lang.assets.assetDeleted, { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(lang.assets.assetDeletedError, { variant: 'error' });
    },
  });
}

export function useCreateAssetTypeMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (assetType: AssetTypeDetail) => {
      return assetsApi.createAssetType(assetType);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['allAssetTypes'], exact: false });
      enqueueSnackbar(lang.assets.assetTypes.createAssetTypeSuccess, { variant: 'success' });
    },
    onError: (error: AxiosError<{ errors: string[] }>) => {
      const errorMessages = error?.response?.data?.errors;
      const isDuplicateName = errorMessages?.some((errorMessage) =>
        errorMessage.includes('duplicate-name'),
      );
      if (isDuplicateName) {
        enqueueSnackbar(lang.assets.assetTypes.createAssetTypeErrorDuplicateName, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(lang.assets.assetTypes.createAssetTypeError, { variant: 'error' });
      }
    },
  });
}

export function useDeleteAssetTypeMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (assetTypeId: number) => assetsApi.deleteAssetType(assetTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['allAssetTypes'], exact: false });
      enqueueSnackbar(lang.assets.assetTypes.assetTypeDeleteSuccess, { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(lang.assets.assetTypes.assetTypeDeleteError, { variant: 'error' });
    },
  });
}

export function useUpdateAssetTypeMutation(options?: { silent?: boolean }) {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: ({ assetTypeId, assetType }: { assetTypeId: number; assetType: AssetTypeDetail }) =>
      assetsApi.updateAssetType(assetTypeId, assetType),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetType', variables.assetTypeId] });
      queryClient.invalidateQueries({ queryKey: ['allAssetTypes'], exact: false });
      if (!silent)
        enqueueSnackbar(lang.assets.assetTypes.assetTypeUpdateSuccess, { variant: 'success' });
    },
    onError: (error: AxiosError<{ errors: string[] }>) => {
      if (silent) return;

      const errorMessages = error?.response?.data?.errors;

      const isDuplicateFieldName = errorMessages?.some((errorMessage) =>
        errorMessage.includes('duplicate-field-name'),
      );

      if (isDuplicateFieldName) {
        enqueueSnackbar(lang.assets.assetTypes.fieldNameAlreadyExists, { variant: 'error' });
      } else {
        enqueueSnackbar(lang.assets.assetTypes.assetTypeUpdateError, { variant: 'error' });
      }
    },
  });
}

interface UseImportAssetsMutationOptions {
  onErrors?: (errors: ImportRowError[]) => void;
  onSheetsWithErrors?: (sheetsWithErrors: string[]) => void;
  onSummary?: (counts: { created: number; restored: number; updated: number }) => void;
}

export function useImportAssetsMutation(options?: UseImportAssetsMutationOptions) {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (file: File) => {
      return assetsApi.importAssets(file);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetsHistory'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetLimitStatus'] });

      if (options?.onErrors && data.errors && data.errors.length > 0) {
        const hasLimitReached = data.errors.some(
          (error) => error.error_type === 'asset_limit_reached',
        );
        options.onErrors(data.errors);
        // When the account hit its limit mid-import the caller surfaces a dedicated
        // modal — skip the success toast and the summary banner so the limit-reached
        // UX is the only thing the user sees, even if some rows were processed.
        if (hasLimitReached) return;
      }

      if (
        options?.onSheetsWithErrors &&
        data.sheets_with_errors &&
        data.sheets_with_errors.length > 0
      ) {
        options.onSheetsWithErrors(data.sheets_with_errors);
      }

      const totalProcessed = (data.created_count ?? 0) + (data.restored_count ?? 0) + (data.updated_count ?? 0);
      if (totalProcessed > 0) {
        enqueueSnackbar(lang.assets.importDrawer.importSuccess, { variant: 'success' });
      }

      if (options?.onSummary) {
        options.onSummary({
          created: data.created_count ?? 0,
          restored: data.restored_count ?? 0,
          updated: data.updated_count ?? 0,
        });
      }
    },
    onError: () => {
      enqueueSnackbar(lang.assets.importDrawer.importError, { variant: 'error' });
    },
  });
}

export function useAssetTypesQuery(search: string, page: number, nameOrder: 'asc' | 'desc') {
  return useQuery({
    queryKey: ['assetTypes', search, page, nameOrder],
    queryFn: () => assetsApi.getAssetTypes({ search, page, name_order: nameOrder }),
  });
}

export function useAllAssetTypesQuery(search: string, nameOrder: 'asc' | 'desc') {
  return useQuery({
    queryKey: ['allAssetTypes', search, nameOrder],
    queryFn: () => assetsApi.getAllAssetTypes({ search, name_order: nameOrder }),
  });
}

export function useAssetTypesWithAssetsQuery(search: string, nameOrder: 'asc' | 'desc' = 'asc') {
  return useQuery({
    queryKey: ['assetTypesWithAssets', search, nameOrder],
    queryFn: () => assetsApi.getAssetTypesWithAssets({ search, name_order: nameOrder }),
  });
}

export function useAssetTypeQuery(assetTypeId: number | undefined) {
  return useQuery({
    queryKey: ['assetType', assetTypeId],
    queryFn: ({ queryKey }) => {
      const [, id] = queryKey as ['assetType', number];
      return assetsApi.getAssetType(id);
    },
    enabled: typeof assetTypeId === 'number' && assetTypeId > 0,
  });
}

export function useAssetLimitStatusQuery() {
  return useQuery({
    queryKey: ['assetLimitStatus'],
    queryFn: () => assetsApi.getAssetLimitStatus(),
  });
}

const INITIALIZE_MIN_MS = 2000;

export function useInitializeAssetTypesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options?: { skipDelay?: boolean }) => {
      if (options?.skipDelay) {
        await assetsApi.initializeAssetTypes();
      } else {
        await Promise.all([
          assetsApi.initializeAssetTypes(),
          new Promise((r) => setTimeout(r, INITIALIZE_MIN_MS)),
        ]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
    },
  });
}

export function useBulkUpdateAssetTypeMutation() {
  const queryClient = useQueryClient();
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ assetIds, assetTypeId }: { assetIds: number[]; assetTypeId: number }) =>
      assetsApi.bulkUpdateAssetType(assetIds, assetTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetsHistory'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypes'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['assetTypesWithAssets'], exact: false });
      enqueueSnackbar(lang.assets.assetUpdated, { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(lang.assets.assetUpdatedError, { variant: 'error' });
    },
  });
}
